import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  Shield,
  RefreshCw,
  AlertCircle,
  DollarSign,
  Layers,
  CreditCard,
  Building2,
  Loader2,
  Activity,
  Copy,
} from 'lucide-react';
import SuperAdminObservability from './SuperAdminObservability';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdminUser } from '../utils/platformRoles';
import PageHeader from '../components/Common/PageHeader';
import FormModalLayout from '../components/Common/FormModalLayout';
import { getAppHomePath, PLATFORM_ADMIN_LABEL } from '../constants/navigation';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../i18n/userFacingError';
import {
  fetchPlatformOverview,
  getPricingSettings,
  updatePricingSettings,
  updatePricingModule,
  getSubscription,
  patchSubscription,
  adminConfirmPayment,
  adminPatchSubscriptionStatus,
  getPricingModules,
  getUserTiers,
  updateUserTier,
  fetchPaymentProviders,
  patchPaymentProvider,
} from '../services/subscriptionService';

function formatBRL(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function saasStatusLabel(s) {
  const m = {
    trial: 'Período de testes',
    active: 'Ativa',
    past_due: 'Em atraso',
    suspended: 'Suspensa (SaaS)',
    cancelled: 'Cancelada',
    none: 'Sem assinatura',
  };
  return m[s] || s;
}

function OrgSubscriptionModal({ org, onClose, onSaved }) {
  const { t } = useTranslation('admin');
  const [sub, setSub] = useState(null);
  const [modulesCatalog, setModulesCatalog] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const orgId = org?.id;

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const [subRes, modRes] = await Promise.all([
        getSubscription(orgId),
        getPricingModules(),
      ]);
      setSub(subRes.data);
      const mods = Array.isArray(modRes.data) ? modRes.data : [];
      setModulesCatalog(mods);
      const codes = subRes.data?.module_codes || ['base'];
      setSelectedModules([...new Set(['base', ...codes.map((c) => String(c).toLowerCase())])]);
      setBillingCycle(subRes.data?.billing_cycle || 'monthly');
    } catch (e) {
      if (e?.response?.status === 404) {
        setSub(null);
        setError('Não há assinatura SaaS cadastrada para esta organização.');
      } else {
        setError(userFacingError(e, 'admin:loadSubscriptionError'));
        setSub(null);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (org) load();
  }, [org, load]);

  const toggleModule = (code) => {
    const c = String(code || '').toLowerCase();
    if (c === 'base') return;
    setSelectedModules((prev) => {
      const set = new Set(prev.map((x) => x.toLowerCase()));
      if (set.has(c)) set.delete(c);
      else set.add(c);
      set.add('base');
      return Array.from(set);
    });
  };

  const savePlan = async () => {
    if (!orgId) return;
    setSaving(true);
    setError('');
    try {
      const module_codes = [...new Set(['base', ...selectedModules.map((x) => String(x).toLowerCase())])];
      const { data } = await patchSubscription(orgId, { module_codes, billing_cycle: billingCycle });
      setSub(data);
      toast.success('Plano atualizado.');
      onSaved?.();
    } catch (e) {
      setError(userFacingError(e, 'admin:savePlanError'));
    } finally {
      setSaving(false);
    }
  };

  const confirmManualPayment = async () => {
    if (!orgId) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await adminConfirmPayment(orgId);
      setSub(data);
      toast.success('Pagamento confirmado manualmente.');
      onSaved?.();
    } catch (e) {
      setError(userFacingError(e, 'admin:confirmPaymentError'));
    } finally {
      setSaving(false);
    }
  };

  const setSaasStatus = async (status) => {
    if (!orgId) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await adminPatchSubscriptionStatus(orgId, status);
      setSub(data);
      toast.success(status === 'active' ? 'Assinatura reativada.' : 'Assinatura suspensa.');
      onSaved?.();
    } catch (e) {
      setError(userFacingError(e, 'admin:updateStatusError'));
    } finally {
      setSaving(false);
    }
  };

  if (!org) return null;

  return (
    <FormModalLayout
      title={t("saasTitle")}
      subtitle={org.name || org.slug}
      onClose={onClose}
      headerIcon={CreditCard}
      size="2xl"
      footer={
        sub && !loading ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <button type="button" className="btn btn-primary" disabled={saving} onClick={savePlan}>
              Salvar plano
            </button>
            <button type="button" className="btn btn-outline" disabled={saving} onClick={confirmManualPayment}>
              Marcar período como pago (manual)
            </button>
            {sub.status === 'suspended' ? (
              <button type="button" className="btn btn-outline" disabled={saving} onClick={() => setSaasStatus('active')}>
                Reativar cobrança SaaS
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-outline"
                disabled={saving || sub.status === 'cancelled'}
                onClick={() => setSaasStatus('suspended')}
              >
                Suspender cobrança SaaS
              </button>
            )}
          </div>
        ) : null
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{error}</p>
        )}
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando…
          </div>
        ) : sub ? (
          <>
            <div className="grid grid-cols-2 gap-2 border-b border-gray-100 pb-4 text-sm">
              <span className="text-gray-600">Status</span>
              <span className="font-medium">{saasStatusLabel(sub.status)}</span>
              <span className="text-gray-600">Valor do período</span>
              <span className="font-medium">{formatBRL(sub.price_period_cents)}</span>
              <span className="text-gray-600">Próximo vencimento</span>
              <span>
                {sub.next_due_at
                  ? new Date(sub.next_due_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                  : '—'}
              </span>
            </div>
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">Módulos</h4>
              <div className="flex flex-wrap gap-2">
                {modulesCatalog
                  .filter((m) => m.is_active !== false)
                  .map((m) => {
                    const on = selectedModules.map((x) => x.toLowerCase()).includes(String(m.code).toLowerCase());
                    const isBase = String(m.code).toLowerCase() === 'base';
                    return (
                      <label
                        key={m.code}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
                          on ? 'border-primary-400 bg-primary-50' : 'border-gray-200'
                        } ${isBase ? 'opacity-80' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={isBase}
                          onChange={() => toggleModule(m.code)}
                        />
                        <span>{m.display_name || m.code}</span>
                      </label>
                    );
                  })}
              </div>
            </div>
            <label className="block max-w-xs">
              <span className="text-sm text-gray-600">Ciclo de cobrança</span>
              <select
                className="form-select mt-1 w-full"
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value)}
              >
                <option value="monthly">Mensal</option>
                <option value="semiannual">Semestral</option>
                <option value="annual">Anual</option>
              </select>
            </label>
          </>
        ) : null}
      </div>
    </FormModalLayout>
  );
}

function PricingAdminPanel() {
  const [modules, setModules] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);
  const [tierEdit, setTierEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mRes, sRes, tRes] = await Promise.all([getPricingModules(), getPricingSettings(), getUserTiers()]);
      setModules(Array.isArray(mRes.data) ? mRes.data : []);
      setSettings(sRes.data);
      setTiers(Array.isArray(tRes.data) ? tRes.data : []);
    } catch (e) {
      setError(userFacingError(e, 'admin:loadPricingError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await updatePricingSettings({
        discount_semiannual_percent: settings.discount_semiannual_percent,
        discount_annual_percent: settings.discount_annual_percent,
        grace_days_after_due: settings.grace_days_after_due,
      });
      toast.success('Configurações globais salvas.');
      await load();
    } catch (e) {
      setError(userFacingError(e, 'admin:saveSettingsError'));
    } finally {
      setSaving(false);
    }
  };

  const saveModule = async (code) => {
    if (!edit || edit.code !== code) return;
    setSaving(true);
    try {
      await updatePricingModule(code, {
        display_name: edit.display_name,
        price_monthly_cents: Number(edit.price_monthly_cents),
        is_active: edit.is_active,
      });
      setEdit(null);
      toast.success('Módulo atualizado.');
      await load();
    } catch (e) {
      setError(userFacingError(e, 'admin:saveModuleError'));
    } finally {
      setSaving(false);
    }
  };

  const saveTier = async () => {
    if (!tierEdit) return;
    setSaving(true);
    try {
      const maxUsers = String(tierEdit.max_users ?? '').trim();
      await updateUserTier(tierEdit.code, {
        display_name: tierEdit.display_name,
        max_users: maxUsers === '' ? null : Number(maxUsers),
        price_monthly_cents: Number(tierEdit.price_monthly_cents),
        sort_order: Number(tierEdit.sort_order) || 0,
        is_active: tierEdit.is_active,
      });
      setTierEdit(null);
      toast.success('Faixa de usuários atualizada.');
      await load();
    } catch (e) {
      setError(userFacingError(e, 'admin:saveModuleError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !modules.length) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        Carregando preços…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
      )}
      <div className="data-list-card p-5 sm:p-6">
        <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
          <Layers className="h-5 w-5" />
          Descontos e tolerância de inadimplência
        </h3>
        {settings && (
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <label className="form-group">
              <span className="form-label">Desconto semestral (%)</span>
              <input
                type="number"
                className="form-input"
                value={settings.discount_semiannual_percent}
                onChange={(e) =>
                  setSettings({ ...settings, discount_semiannual_percent: Number(e.target.value) })
                }
              />
            </label>
            <label className="form-group">
              <span className="form-label">Desconto anual (%)</span>
              <input
                type="number"
                className="form-input"
                value={settings.discount_annual_percent}
                onChange={(e) => setSettings({ ...settings, discount_annual_percent: Number(e.target.value) })}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Dias de graça após vencimento</span>
              <input
                type="number"
                className="form-input"
                value={settings.grace_days_after_due}
                onChange={(e) => setSettings({ ...settings, grace_days_after_due: Number(e.target.value) })}
              />
            </label>
          </div>
        )}
        <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={saving}>
          Salvar configurações globais
        </button>
      </div>

      <div className="data-list-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <DollarSign className="h-5 w-5" />
            Preço mensal por módulo
          </h3>
          <button type="button" className="btn btn-outline btn-sm" onClick={load}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Atualizar
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Código</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Nome</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Mensal</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Ativo</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {modules.map((row) => (
                <tr key={row.code}>
                  <td className="px-4 py-3 font-mono">{row.code}</td>
                  <td className="px-4 py-3">{row.display_name}</td>
                  <td className="px-4 py-3">{formatBRL(row.price_monthly_cents)}</td>
                  <td className="px-4 py-3">{row.is_active ? 'Sim' : 'Não'}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="text-primary-700 hover:underline"
                      onClick={() =>
                        setEdit({
                          code: row.code,
                          display_name: row.display_name,
                          price_monthly_cents: row.price_monthly_cents,
                          is_active: row.is_active,
                        })
                      }
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="data-list-card">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="font-semibold text-gray-900">Faixas de usuários</h3>
            <p className="text-sm text-gray-600">
              Acréscimo mensal por faixa. O limite vale para usuários internos; requerentes do portal não ocupam vaga.
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Código</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Nome</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Limite</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Acréscimo mensal</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Ativa</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tiers.map((row) => (
                <tr key={row.code}>
                  <td className="px-4 py-3 font-mono">{row.code}</td>
                  <td className="px-4 py-3">{row.display_name}</td>
                  <td className="px-4 py-3">{row.max_users ?? 'Ilimitado'}</td>
                  <td className="px-4 py-3">{formatBRL(row.price_monthly_cents)}</td>
                  <td className="px-4 py-3">{row.is_active === false ? 'Não' : 'Sim'}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="text-primary-700 hover:underline"
                      onClick={() =>
                        setTierEdit({
                          code: row.code,
                          display_name: row.display_name,
                          max_users: row.max_users ?? '',
                          price_monthly_cents: row.price_monthly_cents,
                          sort_order: row.sort_order ?? 0,
                          is_active: row.is_active !== false,
                        })
                      }
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
              {tiers.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-gray-500" colSpan={6}>
                    Nenhuma faixa cadastrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {tierEdit && (
        <FormModalLayout
          title={`Editar faixa: ${tierEdit.code}`}
          onClose={() => setTierEdit(null)}
          headerIcon={DollarSign}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-outline" onClick={() => setTierEdit(null)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={saveTier}>
                Salvar
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <label className="form-group">
              <span className="form-label">Nome de exibição</span>
              <input
                className="form-input"
                value={tierEdit.display_name}
                onChange={(e) => setTierEdit({ ...tierEdit, display_name: e.target.value })}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Limite de usuários</span>
              <input
                type="number"
                min={1}
                className="form-input max-w-xs font-mono"
                value={tierEdit.max_users}
                onChange={(e) => setTierEdit({ ...tierEdit, max_users: e.target.value })}
              />
              <span className="mt-1 block text-xs text-gray-500">Deixe vazio para faixa ilimitada.</span>
            </label>
            <label className="form-group">
              <span className="form-label">Acréscimo mensal (centavos)</span>
              <input
                type="number"
                min={0}
                className="form-input max-w-xs font-mono"
                value={tierEdit.price_monthly_cents}
                onChange={(e) => setTierEdit({ ...tierEdit, price_monthly_cents: e.target.value })}
              />
              <span className="mt-1 block text-xs text-gray-500">
                ≈ {formatBRL(Number(tierEdit.price_monthly_cents) || 0)} / mês
              </span>
            </label>
            <label className="form-group">
              <span className="form-label">Ordem de exibição</span>
              <input
                type="number"
                className="form-input max-w-xs font-mono"
                value={tierEdit.sort_order}
                onChange={(e) => setTierEdit({ ...tierEdit, sort_order: e.target.value })}
              />
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="form-check"
                checked={tierEdit.is_active}
                onChange={(e) => setTierEdit({ ...tierEdit, is_active: e.target.checked })}
              />
              Faixa disponível para novas assinaturas
            </label>
          </div>
        </FormModalLayout>
      )}

      {edit && (
        <FormModalLayout
          title={`Editar módulo: ${edit.code}`}
          onClose={() => setEdit(null)}
          headerIcon={DollarSign}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-outline" onClick={() => setEdit(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={() => saveModule(edit.code)} disabled={saving}>
                Salvar
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <label className="form-group">
              <span className="form-label">Nome de exibição</span>
              <input
                className="form-input"
                value={edit.display_name}
                onChange={(e) => setEdit({ ...edit, display_name: e.target.value })}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Preço mensal (centavos)</span>
              <input
                type="number"
                min={0}
                className="form-input max-w-xs font-mono"
                value={edit.price_monthly_cents}
                onChange={(e) => setEdit({ ...edit, price_monthly_cents: e.target.value })}
              />
              <span className="mt-1 block text-xs text-gray-500">
                ≈ {formatBRL(Number(edit.price_monthly_cents) || 0)} / mês
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="form-check"
                checked={edit.is_active}
                onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })}
              />
              Módulo ativo para novas assinaturas
            </label>
          </div>
        </FormModalLayout>
      )}
    </div>
  );
}

const ASAAS_URL = {
  sandbox: 'https://sandbox.asaas.com/api/v3',
  production: 'https://api.asaas.com/v3',
};
const WEBHOOK_PATH = '/api/v1/subscriptions/webhooks/asaas';
const WEBHOOK_EVENTS = ['PAYMENT_CONFIRMED', 'PAYMENT_RECEIVED', 'PAYMENT_OVERDUE'];

function saasWebhookUrl() {
  if (typeof window === 'undefined') return WEBHOOK_PATH;
  return `${window.location.origin}${WEBHOOK_PATH}`;
}

function PaymentProvidersPanel() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    display_name: 'Asaas',
    environment: 'production',
    base_url: ASAAS_URL.production,
    api_key: '',
    webhook_secret: '',
    checkout_success_url: '',
    activate: true,
  });

  const asaas = providers.find((p) => (p.code || '').toLowerCase() === 'asaas') || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await fetchPaymentProviders();
      const list = data?.providers || [];
      setProviders(list);
      const row = list.find((p) => (p.code || '').toLowerCase() === 'asaas');
      if (row) {
        const environment = row.environment === 'sandbox' ? 'sandbox' : 'production';
        setForm((f) => ({
          ...f,
          display_name: row.display_name || 'Asaas',
          environment,
          base_url: row.base_url || ASAAS_URL[environment],
          api_key: '',
          webhook_secret: '',
          checkout_success_url: row.checkout_success_url || '',
          activate: Boolean(row.runtime_active || row.is_default),
        }));
      }
    } catch (e) {
      setError(userFacingError(e, 'admin:loadProvidersError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setEnvironment = (environment) => {
    setForm((f) => ({
      ...f,
      environment,
      base_url: ASAAS_URL[environment],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const credentials = {
        environment: form.environment === 'sandbox' ? 'sandbox' : 'production',
        base_url: form.base_url.trim() || ASAAS_URL[form.environment] || ASAAS_URL.production,
      };
      if (form.api_key.trim()) credentials.api_key = form.api_key.trim();
      if (form.checkout_success_url.trim()) credentials.checkout_success_url = form.checkout_success_url.trim();
      const body = {
        display_name: form.display_name.trim() || 'Asaas',
        enabled: true,
        credentials,
      };
      if (form.activate) body.is_default = true;
      if (form.webhook_secret.trim()) body.webhook_secret = form.webhook_secret.trim();
      await patchPaymentProvider('asaas', body);
      toast.success('Configuração Asaas da plataforma salva.');
      setForm((f) => ({ ...f, api_key: '', webhook_secret: '' }));
      await load();
    } catch (err) {
      toast.error(userFacingError(err, 'admin:saveError'));
    } finally {
      setSaving(false);
    }
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(saasWebhookUrl());
      toast.success('URL do webhook copiada.');
    } catch {
      toast.error('Não foi possível copiar a URL.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        Carregando provedores…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
      ) : null}
      <div>
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <CreditCard className="h-5 w-5" aria-hidden />
          Cobrança SaaS (Asaas)
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Conta Asaas da plataforma para receber a assinatura de todos os clientes. Não precisa selecionar um
          órgão.
        </p>
      </div>

      <form onSubmit={save} className="max-w-2xl space-y-5 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-wrap gap-2">
          {asaas?.has_credentials || asaas?.key_configured ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              API key cadastrada
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900">
              API key ausente
            </span>
          )}
          {asaas?.has_webhook_secret || asaas?.webhook_token_configured ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              Token de webhook cadastrado
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900">
              Token de webhook pendente
            </span>
          )}
          {asaas?.runtime_active ? (
            <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-900">
              Runtime ativo
            </span>
          ) : null}
          {asaas?.environment ? (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
              {asaas.environment === 'sandbox' ? 'Sandbox' : 'Produção'}
            </span>
          ) : null}
        </div>

        <label className="block">
          <span className="text-sm text-gray-600">Nome de exibição</span>
          <input
            className="form-input mt-1 w-full"
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
          />
        </label>

        <fieldset>
          <legend className="text-sm text-gray-600">Ambiente Asaas</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2">
              <input
                type="radio"
                name="asaas-environment"
                checked={form.environment === 'sandbox'}
                onChange={() => setEnvironment('sandbox')}
              />
              <span>Sandbox (homologação)</span>
            </label>
            <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2">
              <input
                type="radio"
                name="asaas-environment"
                checked={form.environment === 'production'}
                onChange={() => setEnvironment('production')}
              />
              <span>Produção</span>
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-500">Use a API key do mesmo ambiente selecionado.</p>
        </fieldset>

        <label className="block">
          <span className="text-sm text-gray-600">URL da API</span>
          <input
            className="form-input mt-1 w-full font-mono text-sm"
            value={form.base_url}
            onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">API key do Asaas</span>
          <input
            className="form-input mt-1 w-full font-mono text-sm"
            type="password"
            autoComplete="new-password"
            value={form.api_key}
            onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
            placeholder={asaas?.key_configured ? 'Deixe em branco para manter a chave já salva' : '$aact_…'}
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">Token do webhook Asaas</span>
          <input
            className="form-input mt-1 w-full font-mono text-sm"
            type="password"
            autoComplete="new-password"
            value={form.webhook_secret}
            onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))}
            placeholder={
              asaas?.webhook_token_configured
                ? 'Deixe em branco para manter o token já salvo'
                : 'asaas-access-token'
            }
          />
          <span className="mt-1 block text-xs text-gray-500">
            Cadastre este token no webhook do Asaas (header asaas-access-token).
          </span>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">URL de retorno após pagamento</span>
          <input
            className="form-input mt-1 w-full font-mono text-sm"
            value={form.checkout_success_url}
            onChange={(e) => setForm((f) => ({ ...f, checkout_success_url: e.target.value }))}
            placeholder="https://useambiental.com.br/login"
          />
        </label>

        <div>
          <span className="text-sm text-gray-600">URL do webhook</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs text-primary-800">
              {saasWebhookUrl()}
            </code>
            <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-1" onClick={copyWebhookUrl}>
              <Copy className="h-4 w-4" aria-hidden />
              Copiar
            </button>
          </div>
          <ul className="mt-2 flex flex-wrap gap-2">
            {WEBHOOK_EVENTS.map((ev) => (
              <li
                key={ev}
                className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 font-mono text-xs text-gray-700"
              >
                {ev}
              </li>
            ))}
          </ul>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            className="form-check"
            checked={form.activate}
            onChange={(e) => setForm((f) => ({ ...f, activate: e.target.checked }))}
          />
          Usar o Asaas como provedor ativo da plataforma
        </label>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar configuração da plataforma'}
        </button>
      </form>
    </div>
  );
}

export default function SuperAdmin() {
  const { t } = useTranslation(['admin', 'nav']);

  const { user } = useAuth();
  const [tab, setTab] = useState('subscriptions');
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [modalOrg, setModalOrg] = useState(null);

  const isSuperAdmin = isSuperAdminUser(user);

  const orgById = useMemo(() => {
    const m = new Map();
    orgs.forEach((o) => m.set(String(o.id), o));
    return m;
  }, [orgs]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ovRes, orgRes] = await Promise.all([
        fetchPlatformOverview(),
        api.get('/api/v1/organizations/platform/list'),
      ]);
      setOverview(ovRes);
      setOrgs(Array.isArray(orgRes.data) ? orgRes.data : []);
    } catch (e) {
      setError(userFacingError(e, 'admin:loadDataError'));
      setOverview(null);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) load();
    else setLoading(false);
  }, [isSuperAdmin, load]);

  const subscriptions = overview?.subscriptions || [];
  const summary = overview?.summary || {};

  const filteredSubs = subscriptions.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const org = orgById.get(String(s.organization_id));
    return (
      String(s.organization_id).toLowerCase().includes(q) ||
      (org?.name || '').toLowerCase().includes(q) ||
      (org?.slug || '').toLowerCase().includes(q) ||
      (s.status || '').toLowerCase().includes(q)
    );
  });

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center px-4 text-center">
        <AlertCircle className="mb-4 h-16 w-16 text-amber-500" />
        <h2 className="text-xl font-semibold text-gray-900">Acesso restrito</h2>
        <p className="mt-2 text-gray-600">Apenas super admin pode acessar esta área.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('nav:platformAdmin')}
        description="Gestão da plataforma: preços globais, assinaturas e pagamentos por organização."
        breadcrumbs={[{ label: 'Início', to: getAppHomePath(user) }, { label: PLATFORM_ADMIN_LABEL }]}
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`btn ${tab === 'subscriptions' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('subscriptions')}
          >
            <Building2 className="mr-1 h-4 w-4" />
            Assinaturas
          </button>
          <button
            type="button"
            className={`btn ${tab === 'pricing' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('pricing')}
          >
            <DollarSign className="mr-1 h-4 w-4" />
            Preços por módulo
          </button>
          <button
            type="button"
            className={`btn ${tab === 'providers' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('providers')}
          >
            <CreditCard className="mr-1 h-4 w-4" />
            Provedores de pagamento
          </button>
          <button
            type="button"
            className={`btn ${tab === 'observability' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('observability')}
          >
            <Activity className="mr-1 h-4 w-4" />
            Observabilidade
          </button>
          {tab === 'subscriptions' && (
            <button type="button" className="btn btn-outline" onClick={load}>
              <RefreshCw className="mr-1 h-4 w-4" />
              Atualizar
            </button>
          )}
        </div>
      </PageHeader>

      {tab === 'pricing' ? (
        <PricingAdminPanel />
      ) : tab === 'providers' ? (
        <PaymentProvidersPanel />
      ) : tab === 'observability' ? (
        <SuperAdminObservability />
      ) : (
        <>
          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          )}

          {summary && Object.keys(summary).length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['Total assinaturas', summary.subscriptions_total],
                ['MRR (centavos)', summary.mrr_cents],
                ['Em atraso', summary.past_due_count],
                ['Suspensas', summary.suspended_count],
              ].map(([label, val]) => (
                <div key={label} className="data-list-card p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {label.includes('MRR') ? formatBRL(val) : val ?? '—'}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="search"
              placeholder="Buscar org, slug ou status…"
              className="form-input flex-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 p-10 text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              Carregando…
            </div>
          ) : (
            <div className="data-list-card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Organização</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status SaaS</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Ciclo</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Valor período</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Próx. venc.</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredSubs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        Nenhuma assinatura encontrada.
                      </td>
                    </tr>
                  ) : (
                    filteredSubs.map((s) => {
                      const org = orgById.get(String(s.organization_id));
                      return (
                        <tr key={s.organization_id}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{org?.name || s.organization_id}</div>
                            {org?.slug && <div className="text-xs text-gray-500">{org.slug}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-medium ${
                                s.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : s.status === 'past_due'
                                    ? 'bg-amber-100 text-amber-900'
                                    : s.status === 'suspended'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {saasStatusLabel(s.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">{s.billing_cycle || '—'}</td>
                          <td className="px-4 py-3">{formatBRL(s.price_period_cents)}</td>
                          <td className="px-4 py-3">
                            {s.next_due_at
                              ? new Date(s.next_due_at).toLocaleDateString('pt-BR')
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                              onClick={() =>
                                setModalOrg(
                                  org || {
                                    id: s.organization_id,
                                    name: s.organization_id,
                                    slug: '',
                                  },
                                )
                              }
                            >
                              <CreditCard className="h-4 w-4" />
                              Gerenciar
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modalOrg && (
        <OrgSubscriptionModal org={modalOrg} onClose={() => setModalOrg(null)} onSaved={load} />
      )}
    </div>
  );
}
